import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Meta } from '@angular/platform-browser';
import { Platform, PopoverController, ToastController, AlertController, LoadingController, NavController, IonLoading, ModalController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { NativeService } from './native.service';
import { TeamsService } from './local-data/teams.service';
import { GamesService } from './local-data/games.service';
import { GameTrackerService } from './game-tracker/game-tracker.service';
import { StorageService } from './local-data/storage.service';
import { FilesService } from './local-data/files.service';
import { VideoCacheService } from './local-data/video-cache.service';
import { HlsCaptureService } from './hls/hls-capture.service';
import { IntegrationsService } from './integrations/integrations.service';
import { EliteConfigurationService } from './local-data/elite-configuration.service';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { ApiProvider } from './api.provider';
import { AuthService } from './auth/auth.service';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { VersionService } from './version.service';
import { AnalyticsProvider } from './analytics.provider';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from '../i18n/i18n.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

export async function fetchTimeout(input: string | URL | globalThis.Request, init?: RequestInit, timeout: number = 3000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort('Local connection timeout'), timeout);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

@Injectable({
  providedIn: 'root',
})
export class CoreProvider {

  public readonly backDeviceSearchRegex = /back|trás|rear|traseira|environment|ambiente/gi;

  public readonly isAndroid: boolean = false;
  public readonly isIOS: boolean = false;
  public readonly isWeb: boolean = false;
  public readonly appIsReady$ = new BehaviorSubject<boolean|'error'>(false);

  constructor(
    private screenOrientation: ScreenOrientation,
    public readonly appVersionPlugin: AppVersion,
    public readonly meta: Meta,
    public readonly navCtrl: NavController,
    public readonly toastCtrl: ToastController,
    public readonly alertCtrl: AlertController,
    public readonly modalCtrl: ModalController,
    public readonly popoverCtrl: PopoverController,
    public readonly loadingCtrl: LoadingController,
    public readonly router: Router,
    public readonly platform: Platform,
    public readonly native: NativeService,
    public readonly storage: StorageService,
    public readonly ldTeamsService: TeamsService,
    public readonly ldGamesService: GamesService,
    public readonly ldFilesService: FilesService,
    public readonly ldVideoCacheService: VideoCacheService,
    public readonly hlsCaptureService: HlsCaptureService,
    public readonly integrationsService: IntegrationsService,
    public readonly ldEliteConfigurationService: EliteConfigurationService,
    public readonly gameTrackerService: GameTrackerService,
    public readonly socialSharing: SocialSharing,
    public readonly api: ApiProvider,
    public readonly auth: AuthService,
    public readonly versionService: VersionService,
    public readonly analytics: AnalyticsProvider,
    // Named `trans` to match the sibling hai-app repo, where components call
    // `this.core.trans.instant(_('...'))`.
    public readonly trans: TranslateService,
    public readonly i18nService: I18nService,
  ) {
    const isNative = this.platform.is('cordova') || this.platform.is('capacitor') || this.platform.is('electron');
    this.isAndroid = this.platform.is('android') && isNative;
    this.isIOS = this.platform.is('ios') && isNative;
    this.isWeb = !this.isAndroid && !this.isIOS;
    if (!environment.production && window)
      (window as unknown as { core: CoreProvider })['core'] = this;
    this.init();
  }

  private appVersion?: string;
  public get version() {
    return this.appVersion || '⏣.⏣.⏣';
  }

  async lockOrientationLandscape() {
    if (!this.isAndroid && !this.isIOS) return;
    try {
      await this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);
    } catch (error) {
      console.warn('Could not lock screen orientation to landscape', error);
    }
  }

  unlockOrientation() {
    if (!this.isAndroid && !this.isIOS) return;
    try {
      this.screenOrientation.unlock();
    } catch (error) {
      console.warn('Could not unlock screen orientation', error);
    }
  }

  async init() {
    try {
      // First, so every later step (and the first render) already has the
      // resolved language available to `trans.instant()`.
      await this.initTranslations();

      await this.native.initCore(this);
      // if (this.isAndroid || this.isIOS) {
      //   await this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);
      // }
      await this.storage.initCore(this);
      await this.api.setCore(this);
      await this.auth.initCore(this);
      await this.ldTeamsService.initCore(this);
      await this.ldGamesService.initCore(this);
      await this.ldFilesService.initCore(this);
      // After files (it uses them) and before the game tracker (which drives its lifecycle)
      await this.ldVideoCacheService.initCore(this);
      // Same slot and same reasoning: it produces clips through the files service, and the game tracker
      // owns when it is attached to a stream and when it is torn down.
      await this.hlsCaptureService.initCore(this);
      await this.integrationsService.initCore(this);
      await this.ldEliteConfigurationService.initCore(this);
      await this.gameTrackerService.initCore(this);

      if (this.isAndroid || this.isIOS) {
        try {
          this.appVersion = await this.appVersionPlugin.getVersionNumber();
        } catch (error) {
          console.error('Error getting native version', error);
        }
      }
      if (!this.appVersion) { // Fallback to meta tag if plugin is not available
        console.log('No app version found, using meta tag', this.meta.getTags('version'));
        this.appVersion = this.meta.getTag('name="version"')?.content;
      }

      // We needs `this.appVersion` already resolved to compare against the released one.
      await this.versionService.initCore(this);

      // Last, so queued events are flushed with the platform and version context already resolved.
      this.analytics.initCore(this);
      this.appIsReady$.next(true);
    } catch (error) {
      console.error('Error initializing app', error);
      this.appIsReady$.next('error');
    }
  }


  /**
   * Loads the UI language before anything else runs.
   *
   * Owned by I18nService so the `use()` call happens exactly once — see the
   * note there on why a second `use()` for the same language silently no-ops.
   */
  private async initTranslations(): Promise<void> {
    await this.i18nService.init();
  }

  public getStripeReturnUrl(success: boolean) {
    return this.isWeb
      ? window.location.origin + '/configuration/user-profile'
      : 'https://app.haibench.ai' + '/payment-confirmation?success=' + success; // Doesn't matter to put everytime production, is only an information page
  }
  public openExternalUrl(url: string, target: '_blank' | '_self' = '_blank') {
    if (this.isWeb) {
      window.open(url, target);
    } else if (this.isAndroid || this.isIOS) {
      // Try to open with system browser (default installed browser)
      try {
        // In Cordova, window.open with _system targets the external browser
        const opened = window.open(url, '_system');
        if (opened !== null) return;
      } catch {}

      // Fallback to InAppBrowser with _system target if available
      const w = window as any;
      try {
        if (w.cordova && w.cordova.InAppBrowser) {
          w.cordova.InAppBrowser.open(url, '_system');
          return;
        } else {
          const opened = window.open(url, '_blank');
          if (opened !== null) return;
        }
      } catch {}
    } else {
      // Last resort
      window.open(url, '_blank');
    }
  }

  public async genericLoading(message?: string, duration: number|null = 30000) {
    const loading = await this.loadingCtrl.create({
      message: message || this.trans.instant(_('Loading...')),
      mode: 'ios',
      duration: duration ?? undefined,
      spinner: 'lines-small',
    });
    await loading.present();
    return loading;
  }
  public async successToast(loading?: HTMLIonLoadingElement | LoadingStatusHandler, message?: string) {
    if (loading) {
      if ('dismiss' in loading && typeof loading.dismiss == 'function') loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setSuccess();
    }

    while (await this.toastCtrl.getTop()) {
      await this.toastCtrl.dismiss();
    }

    await (
      await this.toastCtrl.create({
        message: message || this.trans.instant(_('The action completed successfully')),
        duration: 3000,
        color: 'success',
      })
    ).present();
  }

  public async errorToast(
    loading?: HTMLIonLoadingElement | LoadingStatusHandler,
    message: any | string = null,
    duration = null
  ) {
    if (loading) {
      if ('dismiss' in loading && typeof loading.dismiss == 'function') loading.dismiss();
      if (loading instanceof LoadingStatusHandler) loading.setError();
    }

    if (typeof message != 'string' && message != null) {
      // if (
      //   message.status &&
      //   message.status == 401 &&
      //   !this.auth.isAuthenticated
      // ) {
      //   this.auth.logout();
      // }
      message = message instanceof Error ? message.message : this.fetchErrMsg(message.error);
    }

    while (await this.toastCtrl.getTop()) {
      await this.toastCtrl.dismiss();
    }

    return await (
      await this.toastCtrl.create({
        message:
          message || this.trans.instant(_('An error occurred, please try again later')),
        duration: duration || 10000,
        color: 'danger',
        buttons: [{ text: this.trans.instant(_('OK')), role: 'cancel' }],
      })
    ).present();
  }
  public fetchErrMsg(err: any) {
    if (typeof err == 'string') {
      try {
        err = JSON.parse(err);
      } catch (e) {
        return err;
      }
    }

    if (err.message?.[0]?.constraints) return err.message.reduce((acc: string, itm: any) =>
      acc + '\n' + Object.keys(itm.constraints).reduce((acc2: string, key: string) =>
        acc2 + '\n - ' + itm.constraints[key]
      , '')
    , '');
    if (err.error && err.message) return `${err.error}: ${err.message}`;
    if (err.error) return err.error;
    if (err.errors) {
      let msg = this.trans.instant(_('The following errors occurred: '));
      for (let itm in err.errors) {
        msg += '\n- ' + err.errors[itm].toString();
      }
      return msg;
    }
    if (Object.getPrototypeOf(err).toString().indexOf('ProgressEvent'))
      return this.trans.instant(_('A communication error occurred, please check your connection.'));
    return err.toString();
  }
}

export type LoadingStatusType = 'error' | 'loading' | 'success' | 'idle';
export class LoadingStatusHandler extends BehaviorSubject<LoadingStatusType> {
  timeout: any;
  constructor(initial: LoadingStatusType = 'idle') {
    super(initial);
  }
  override next(value: LoadingStatusType): void {
    throw new Error('Setting status directly is not allowed');
  }
  setIdle() {
    super.next('idle');
    this.clearTimeout();
  }
  setLoading() {
    super.next('loading');
    this.clearTimeout();
  }
  setError() {
    super.next('error');
    this.clearTimeout();
  }
  setSuccess() {
    super.next('success');
    this.clearTimeout();
    this.timeout = setTimeout(() => {
      this.setIdle();
    }, 3000);
  }

  private clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}
